import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Compass, Plus, Search, Filter, ExternalLink, Edit, Trash2,
  MapPin, Clock, Globe, Play, Upload, Image as ImageIcon,
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import TourViewer from '../../components/common/TourViewer';
import { virtualToursAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import { hasPermission, PERMISSIONS } from '../../utils/rbac';
import { BASE_URL, getStaticUrl } from '../../constants/config';
import './VirtualTours.css';

const PLACEHOLDER_IMAGE = `${BASE_URL}/uploads/assets/placehold_content.svg`;

const EMPTY_FORM = {
  title: '', subtitle: '', description: '', tour_url: '', thumbnail_url: '',
  category: 'Onboarding', location: '', duration: '', tags: '', status: 'Active',
};

const VirtualTours = () => {
  const navigate = useNavigate();
  const { showNotification, currentUser } = useAppContext();
  const canManage = hasPermission(currentUser, PERMISSIONS.MANAGE_VIRTUAL_TOURS);

  const [tours, setTours] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  // Add / edit modal
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const thumbInputRef = useRef(null);

  // Embedded viewer (popup iframe)
  const [viewing, setViewing] = useState(null);

  const loadTours = useCallback(async () => {
    setLoading(true);
    try {
      const res = await virtualToursAPI.getTours();
      setTours(res.data?.data || []);
    } catch (err) {
      showNotification('Failed to load virtual tours', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { loadTours(); }, [loadTours]);

  useEffect(() => {
    virtualToursAPI.getCategories()
      .then(res => setCategories(res.data?.data || []))
      .catch(() => {});
  }, []);

  const goToDetail = (tour) => {
    const idForUrl = tour.tourId ? tour.tourId.replace(/-/g, '') : tour.id;
    navigate(`/virtual-tours/${idForUrl}`, { state: { fromList: true } });
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (tour, e) => {
    e?.stopPropagation();
    setEditing(tour);
    setForm({
      title: tour.title || '',
      subtitle: tour.subtitle || '',
      description: tour.description || '',
      tour_url: tour.tourUrl || '',
      thumbnail_url: tour.thumbnail || '',
      category: tour.category || 'Onboarding',
      location: tour.location || '',
      duration: tour.duration != null ? String(tour.duration) : '',
      tags: (tour.tags || []).join(', '),
      status: tour.status || 'Active',
    });
    setFormOpen(true);
  };

  const handleThumbFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      showNotification('Please select a JPEG, PNG, GIF, or WEBP image', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showNotification('Image must be under 10MB', 'error');
      return;
    }
    setUploadingThumb(true);
    try {
      const fd = new FormData();
      fd.append('thumbnail', file);
      const res = await virtualToursAPI.uploadImage(fd);
      const url = res.data?.data?.url;
      if (url) {
        setForm(f => ({ ...f, thumbnail_url: url }));
        showNotification('Image uploaded', 'success');
      }
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to upload image', 'error');
    } finally {
      setUploadingThumb(false);
      if (thumbInputRef.current) thumbInputRef.current.value = '';
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.tour_url.trim()) {
      showNotification('Title and tour URL are required', 'warning');
      return;
    }
    const payload = {
      title: form.title.trim(),
      subtitle: form.subtitle.trim() || null,
      description: form.description.trim(),
      tour_url: form.tour_url.trim(),
      thumbnail_url: form.thumbnail_url.trim() || null,
      category: form.category,
      location: form.location.trim() || null,
      duration: form.duration ? parseInt(form.duration, 10) : null,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      status: form.status,
    };
    setSubmitting(true);
    try {
      if (editing) {
        await virtualToursAPI.updateTour(editing.tourId.replace(/-/g, ''), payload);
        showNotification('Virtual tour updated', 'success');
      } else {
        await virtualToursAPI.createTour(payload);
        showNotification('Virtual tour created', 'success');
      }
      setFormOpen(false);
      loadTours();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      showNotification(msg || 'Failed to save virtual tour', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (tour, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete virtual tour "${tour.title}"? This cannot be undone.`)) return;
    try {
      await virtualToursAPI.deleteTour(tour.tourId.replace(/-/g, ''));
      showNotification('Virtual tour deleted', 'success');
      loadTours();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const filtered = tours.filter((t) => {
    if (categoryFilter !== 'All' && t.category !== categoryFilter) return false;
    if (statusFilter !== 'All' && t.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const inTags = (t.tags || []).some(tag => tag.toLowerCase().includes(q));
      if (!t.title.toLowerCase().includes(q) &&
          !(t.subtitle || '').toLowerCase().includes(q) &&
          !(t.description || '').toLowerCase().includes(q) &&
          !(t.location || '').toLowerCase().includes(q) &&
          !inTags) return false;
    }
    return true;
  });

  const thumbPreview = form.thumbnail_url ? getStaticUrl(form.thumbnail_url) : '';

  return (
    <div className="vt-page">
      {/* Header */}
      <div className="vt-header">
        <div className="vt-header__text">
          <h1><Compass size={22} /> Virtual Tours</h1>
          <p>Explore curated web-based 360° virtual tours of clinical facilities and training environments.</p>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={openAdd}>
            <Plus size={17} /> Add Tour
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="vt-filters card">
        <div className="vt-search">
          <Search size={16} className="vt-search__icon" />
          <input
            type="text"
            placeholder="Search tours by title, location, tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="vt-filter-group">
          <Filter size={14} />
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="All">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {canManage && (
          <div className="vt-filter-group">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Draft">Draft</option>
            </select>
          </div>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="vt-loading">
          <div className="vt-spinner" />
          <p>Loading virtual tours...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="vt-empty">
          <div className="vt-empty__icon"><Globe size={28} /></div>
          <h3>No virtual tours found</h3>
          <p>
            {canManage
              ? 'Add your first web-based virtual tour to get started.'
              : 'Check back soon — new virtual tours will appear here.'}
          </p>
        </div>
      ) : (
        <div className="vt-grid">
          {filtered.map((tour) => (
            <div key={tour.tourId} className="vt-card card" onClick={() => goToDetail(tour)}>
              <div className="vt-card__thumb">
                <img src={getStaticUrl(tour.thumbnail) || PLACEHOLDER_IMAGE} alt={tour.title}
                  onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }} />
                <button
                  className="vt-card__launch"
                  title="Launch tour"
                  onClick={(e) => { e.stopPropagation(); setViewing(tour); }}
                >
                  <Play size={15} />
                </button>
                {tour.status === 'Draft' && <span className="vt-card__draft">Draft</span>}
              </div>
              <div className="vt-card__body">
                <div className="vt-card__top">
                  <span className="badge badge-green">{tour.category}</span>
                  <span className="badge badge-dark">🌐 Web Tour</span>
                </div>
                <h3 className="vt-card__title">{tour.title}</h3>
                {tour.subtitle && <p className="vt-card__subtitle">{tour.subtitle}</p>}
                {tour.description && <p className="vt-card__desc">{tour.description}</p>}
                {tour.tags?.length > 0 && (
                  <div className="vt-card__tags">
                    {tour.tags.slice(0, 3).map(t => <span key={t} className="vt-tag">#{t}</span>)}
                  </div>
                )}
                <div className="vt-card__footer">
                  {tour.location && <span><MapPin size={12} /> {tour.location}</span>}
                  {tour.duration != null && <span><Clock size={12} /> {tour.duration} min</span>}
                </div>
              </div>
              {canManage && (
                <div className="vt-card__actions" onClick={(e) => e.stopPropagation()}>
                  <button className="vt-icon-btn" title="Edit" onClick={(e) => openEdit(tour, e)}>
                    <Edit size={15} />
                  </button>
                  <button className="vt-icon-btn vt-icon-btn--danger" title="Delete" onClick={(e) => handleDelete(tour, e)}>
                    <Trash2 size={15} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Popup iframe viewer */}
      <TourViewer tour={viewing} onClose={() => setViewing(null)} />

      {/* Add / Edit modal */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? 'Edit Virtual Tour' : 'Add Virtual Tour'}
        size="md"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setFormOpen(false)} disabled={submitting}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={submit} disabled={submitting}>
              {submitting ? 'Saving...' : (editing ? 'Save Changes' : 'Add Tour')}
            </button>
          </>
        }
      >
        <form className="vt-form" onSubmit={submit}>
          <label className="vt-field">
            <span>Title *</span>
            <input type="text" required value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Bonny Island LNG Plant Walkthrough" />
          </label>
          <label className="vt-field">
            <span>Subtitle</span>
            <input type="text" value={form.subtitle}
              onChange={(e) => setForm(f => ({ ...f, subtitle: e.target.value }))}
              placeholder="Short tagline (optional)" />
          </label>
          <label className="vt-field">
            <span>Tour URL *</span>
            <input type="url" required value={form.tour_url}
              onChange={(e) => setForm(f => ({ ...f, tour_url: e.target.value }))}
              placeholder="https://..." />
          </label>

          {/* Thumbnail: upload a file OR paste a URL */}
          <div className="vt-field">
            <span>Thumbnail Image</span>
            <div className="vt-thumb-uploader">
              <div className="vt-thumb-preview">
                {thumbPreview
                  ? <img src={thumbPreview} alt="Thumbnail preview" onError={(e) => { e.target.style.display = 'none'; }} />
                  : <ImageIcon size={22} />}
              </div>
              <div className="vt-thumb-controls">
                <input
                  type="file"
                  accept="image/*"
                  ref={thumbInputRef}
                  style={{ display: 'none' }}
                  onChange={handleThumbFile}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => thumbInputRef.current?.click()}
                  disabled={uploadingThumb}
                >
                  <Upload size={14} /> {uploadingThumb ? 'Uploading...' : 'Upload image'}
                </button>
                {form.thumbnail_url && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() => setForm(f => ({ ...f, thumbnail_url: '' }))}
                    disabled={uploadingThumb}
                  >
                    Remove
                  </button>
                )}
                <input
                  type="url"
                  className="vt-thumb-url"
                  value={form.thumbnail_url}
                  onChange={(e) => setForm(f => ({ ...f, thumbnail_url: e.target.value }))}
                  placeholder="…or paste an image URL"
                />
              </div>
            </div>
          </div>

          <div className="vt-form-row">
            <label className="vt-field">
              <span>Category *</span>
              <select value={form.category}
                onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}>
                {(categories.length ? categories : ['Onboarding', 'Technical', 'Safety', 'Certification', 'Marine'])
                  .map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="vt-field">
              <span>Status</span>
              <select value={form.status}
                onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </label>
          </div>
          <div className="vt-form-row">
            <label className="vt-field">
              <span>Location</span>
              <input type="text" value={form.location}
                onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder="e.g. Bonny Island" />
            </label>
            <label className="vt-field">
              <span>Duration (min)</span>
              <input type="number" min="0" value={form.duration}
                onChange={(e) => setForm(f => ({ ...f, duration: e.target.value }))}
                placeholder="e.g. 10" />
            </label>
          </div>
          <label className="vt-field">
            <span>Description</span>
            <textarea rows={3} value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What does this tour cover?" />
          </label>
          <label className="vt-field">
            <span>Tags <span className="vt-muted">(comma separated)</span></span>
            <input type="text" value={form.tags}
              onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
              placeholder="safety, plant, 360" />
          </label>
        </form>
      </Modal>
    </div>
  );
};

export default VirtualTours;
